import styles from './ImmigrationStatus.module.scss';
import { useFormatDate } from 'src/utils/hooks/useFormatDate';
import { getCountryName } from 'src/utils/services/common';
import { CountryCode, FallbackPlaceholderText } from 'src/utils/constants/common';
import { IImmigrationStatusInfo } from 'src/shared/types/ImmigrationStatus';
import { useGetImmigrationStatusList } from 'src/shared/queries/useGetImmigrationStatusList';
import { useGetImmigrationStatusClassification } from 'src/shared/queries/useGetImmigrationStatusClassification';
import { useGetCountries } from 'src/shared/queries/useGetCountries';
import Passports from './Passports/Passports';
import ImmigrationDocuments from './ImmigrationDocuments/ImmigrationDocuments';

const TabContent = (details: {
  beneId: number | undefined;
  dependentId: number | undefined;
  dob: string;
  cob: string;
  isBeneficiary: boolean;
  statusInfo: IImmigrationStatusInfo | undefined;
  showActiveOnly: boolean;
}) => {
  const { data: immigrationStatusList } = useGetImmigrationStatusList();
  const { data: classificationValues } = useGetImmigrationStatusClassification(CountryCode.UnitedStates, true);
  const { data: countries } = useGetCountries();

  const getDurationByClassification = (statusName: string) => {
    return (
      classificationValues?.find(({ classificationName }) => classificationName === statusName)?.classificationLength ||
      0
    );
  };

  const calculateRecaptureDays = (statusName: string, initialDateStr: string, finalDateStr: string) => {
    if (!statusName || !initialDateStr || !finalDateStr) return 0;

    const validClassifications = classificationValues
      ?.filter(({ classificationLength }) => classificationLength > 0)
      ?.map(({ classificationName }) => classificationName);
    if (!validClassifications || !validClassifications.includes(statusName)) return 0;

    const initialDate = new Date(initialDateStr);
    const finalDate = new Date(finalDateStr);
    const duration = getDurationByClassification(statusName);
    const initialDateToCompare = new Date(initialDate);
    initialDateToCompare.setFullYear(initialDate.getFullYear() + duration);
    initialDateToCompare.setDate(initialDate.getDate() - 1);

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const recapturedDays = Math.floor((finalDate.getTime() - initialDateToCompare.getTime()) / millisecondsPerDay);

    return Math.max(recapturedDays, 0);
  };

  return (
    <div className={`flex flex-col gap-5 ${styles['tab-content']}`}>
      <div className={`flex flex-col md:flex-row pt-2 pb-2 gap-4 ${styles['tab-content__dob-cob']}`}>
        <div className={`flex items-center md:mr-20`}>
          <div className={`data-field`}>
            Date of Birth: {useFormatDate(details.dob) || FallbackPlaceholderText.SingleDash}
          </div>
        </div>
        <div className="flex items-center">
          <div className={`data-field`}>
            Country/Location of Birth:{' '}
            {(countries && getCountryName(countries, details.cob)) || FallbackPlaceholderText.SingleDash}
          </div>
        </div>
      </div>

      <h3 className="mt-2 mb-2">United States</h3>

      <div className={`flex flex-wrap flex-col md:flex-row mb-4 ${styles['tab-content__immigration-info']}`}>
        <div className={`flex items-center w-full md:max-w-[280px] p-4`}>
          <div className={styles['data-label']}>
            Current Status
            <div className={styles['data-value']}>
              {immigrationStatusList?.find((_) => _.id == details.statusInfo?.immigrationStatus)?.status ||
                FallbackPlaceholderText.SingleDash}
            </div>
          </div>
        </div>
        <div className="flex items-center w-full md:max-w-[280px] p-4">
          <div className={styles['data-label']}>
            Previous Status
            <div className={styles['data-value']}>
              {immigrationStatusList?.find((_) => _.id == details.statusInfo?.previousStatus)?.status ||
                FallbackPlaceholderText.SingleDash}
            </div>
          </div>
        </div>
        <div className="flex items-center w-full md:max-w-[280px] p-4">
          <div className={styles['data-label']}>
            Future Status
            <div className={styles['data-value']}>
              {immigrationStatusList?.find((_) => _.id == details.statusInfo?.futureStatus)?.status ||
                FallbackPlaceholderText.SingleDash}
            </div>
          </div>
        </div>
        {details.isBeneficiary && (
          <>
            <div className="flex items-center w-full md:max-w-[280px] p-4">
              <div className={styles['data-label']}>
                1st H/L
                <div className={styles['data-value']}>
                  {useFormatDate(details.statusInfo?.firstEntryDate) || FallbackPlaceholderText.SingleDash}
                </div>
              </div>
            </div>
            <div className="flex items-center w-full md:max-w-[280px] p-4">
              <div className={styles['data-label']}>
                I-94 Final Date
                <div className={styles['data-value']}>
                  {useFormatDate(details.statusInfo?.i94FinalDate) || FallbackPlaceholderText.SingleDash}
                </div>
              </div>
            </div>
            <div className="flex items-center w-full md:max-w-[280px] p-4">
              <div className={styles['data-label']}>
                Days Recaptured
                <div className={styles['data-value']}>
                  {calculateRecaptureDays(
                    immigrationStatusList?.find((_) => _.id == details.statusInfo?.immigrationStatus)?.status ?? '',
                    details.statusInfo?.firstEntryDate ?? '',
                    details.statusInfo?.i94FinalDate ?? ''
                  ) || FallbackPlaceholderText.SingleDash}
                </div>
              </div>
            </div>
          </>
        )}
        <div className="flex items-center w-full md:max-w-[280px] p-4">
          <div className={styles['data-label']}>
            A Number{' '}
            <div className={styles['data-value']}>
              {details.statusInfo?.aNumber || FallbackPlaceholderText.SingleDash}
            </div>
          </div>
        </div>
      </div>

      <Passports beneId={details.beneId} dependentId={details.dependentId} showActiveOnly={details.showActiveOnly} />

      <ImmigrationDocuments
        beneId={details.beneId}
        dependentId={details.dependentId}
        showActiveOnly={details.showActiveOnly}
      />
    </div>
  );
};

export default TabContent;
